import React, { useEffect, useRef, useState } from 'react';
import {
  Box,
  Image,
  IconButton,
  useToast,
  VStack,
  Button,
  Text,
  BoxProps,
  HStack,
} from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';
import { CheckIcon, CloseIcon } from '@chakra-ui/icons';
import { getCoords } from './shared/utils';
import { colors } from '../../../../../App/ChakraTheme';
import classNames from 'classnames';
import { RefreshIcon } from '../../../../../components/icons';

type Point = {
  x: string | number;
  y: string | number;
};

type Spot = {
  key: string;
  type: 'Rectangle' | 'Circle' | 'Shape';
  point?: Point;
  radius?: string | number;
  points?: Point[];
  answer: string;
  caseSensitive?: string;
  showArea?: boolean;
};

interface ImageClickHotspotsProps extends BoxProps {
  src: string;
  altText?: string;
  spots: Spot[];
  debug?: boolean;
  showCorrect?: boolean;
  isViewOnly?: boolean;
  submit?: (correct: number, total: number, state: any) => void;
  reset?: () => void;
  initialState?: ContainerState['state'];
}

export interface ContainerState {
  state: {
    [key: string]: {
      title: string;
      correct: boolean;
      x?: string;
      y?: string;
    };
  };
}

const CheckIconComponent: React.FC<{ point: Point; animate: boolean }> = ({
  point,
  animate,
}) => {
  const self = useRef<SVGSVGElement | null>(null);
  useEffect(() => {
    // if (animate)
    setTimeout(() => {
      if (self.current) {
        self.current.classList.add('animate');
      }
    }, 0);
  }, []);

  return (
    <CheckIcon
      ref={self}
      sx={{
        '&': {
          top: point.y,
          left: point.x,
        },
        '&.finished': {
          transform: 'translate(-50%,-50%) scale(1)',
        },
        '&.animate, &.animate:not(.finished)': {
          transform: 'translate(-50%,-50%) scale(1)',
        },
        '&:not(.finished)': {
          transform: 'translate(-50%,-50%) scale(0)',
        },
      }}
      color="#fff"
      borderRadius="100%"
      backgroundColor={colors.limegreen}
      className={classNames({
        finished: !animate,
      })}
      position="absolute"
      transition="transform 250ms ease-in-out"
      pointerEvents="none"
      boxSize="32px"
      p={2}
      boxShadow="2px 2px 6px rgba(0,0,0,0.65)"
    />
  );
};

const XIconComponent: React.FC<{
  point: Point;
  state: 'enter' | 'show' | 'exiting' | 'no-show' | string;
  updateSelf: (state: string) => void;
  removeSelf: () => void;
}> = ({ point, state, updateSelf, removeSelf }) => {
  const self = useRef<SVGSVGElement | null>(null);

  useEffect(() => {
    setTimeout(() => {
      if (self.current) {
        self.current.classList.add('animate');
        self.current.classList.add('finished');
      }
    }, 0);
    setTimeout(() => {
      if (self.current) {
        self.current.classList.remove('finished');
        self.current.classList.remove('animate');
      }
    }, 5000);
  }, []);

  return (
    <CloseIcon
      ref={self}
      sx={{
        '&': {
          top: point.y,
          left: point.x,
        },
        '&.finished': {
          transform: 'translate(-50%,-50%) scale(1)',
        },
        '&.animate, &.animate:not(.finished)': {
          transform: 'translate(-50%,-50%) scale(1)',
        },
        '&:not(.finished)': {
          transform: 'translate(-50%,-50%) scale(0)',
        },
      }}
      color="#fff"
      borderRadius="100%"
      backgroundColor={colors.tomato}
      className={classNames({
        finished: state === 'show' || state === 'exiting',
      })}
      position="absolute"
      transition="transform 250ms ease-in-out"
      pointerEvents="none"
      boxSize="32px"
      p={2}
      boxShadow="2px 2px 6px rgba(0,0,0,0.65)"
    />
  );
};

type IncorrectSpots = {
  point: Point;
  state: 'enter' | 'show' | 'exiting' | 'no-show' | string;
};

const ImageHotspots: React.FC<ImageClickHotspotsProps> = ({
  spots,
  src,
  altText,
  debug,
  showCorrect,
  submit,
  reset,
  initialState,
  isViewOnly,
  ...rest
}) => {
  const rootRef = useRef<HTMLDivElement | null>(null);
  const [state, updateState] = useState<ContainerState['state']>({});
  const [showResults, setShowResults] = useState<boolean>(false);
  const [shownCorrect, setShownCorrect] = useState<number>(0);
  const [foundSpots, setFoundSpots] = useState<Point[]>([]);
  const [incorrectFoundSpots, setIncorrectFoundSpots] = useState<
    IncorrectSpots[]
  >([]);
  const [timeout, setSetTimeout] = useState<NodeJS.Timeout | null>(null);
  const toast = useToast();
  const { t } = useTranslation();

  const setInitialState = () => {
    const state: ContainerState['state'] = {};

    spots.forEach((spot, index) => {
      state[spot.key] = {
        correct: false,
        title: spot.answer,
      };
    });

    updateState(state);
  };

  useEffect(() => {
    if (initialState) {
      updateState(initialState);
      setShowResults(true);
      const found: Point[] = [];
      Object.keys(initialState).forEach((key) => {
        if (initialState[key]!.correct) {
          const point = { x: initialState[key]!.x!, y: initialState[key]!.y! };
          found.push(point);
        }
      });
      if (found.length > 0) {
        setFoundSpots(found);
        setShownCorrect(found.length);
      }
    } else setInitialState();
  }, [spots, initialState]);

  const _submit = () => {
    setShowResults(true);
    setShownCorrect(foundSpots.length);
    if (submit != null) {
      submit(foundSpots.length, spots.length, state);
    }
  };

  const _reset = () => {
    setInitialState();
    setFoundSpots([]);
    setShowResults(false);
    setShownCorrect(0);
    if (reset != null) reset();
  };

  const handleSpotActivation = (spot: Spot, point: Point) => {
    const newState = { ...state };
    const firstClick = !newState[spot.key]!.correct;

    if (firstClick && rootRef.current) {
      const newFounds = [...foundSpots];
      newFounds.push(point);
      setFoundSpots(newFounds);
      toast({
        title: t('youDidIt'),
        description: t('youClickedIt', { answer: spot.answer }),
        status: 'success',
        duration: 3000,
        isClosable: true,
      });

      newState[spot.key]!.correct = true;
      newState[spot.key]!.x = point.x as string;
      newState[spot.key]!.y = point.y as string;
      updateState(newState);
    }
  };

  const ret = (
    <VStack>
      <Box position="relative" {...rest} cursor="pointer" ref={rootRef}>
        <Image
          src={src}
          alt={altText ?? ''}
          userSelect="none"
          onClick={(e) => {
            if (timeout != null) {
              clearTimeout(timeout);
            }
            setSetTimeout(
              setTimeout(() => {
                setIncorrectFoundSpots([]);
              }, 5500),
            );
            if (rootRef.current) {
              const cords = getCoords(rootRef.current);

              const incorrectSpots = [...incorrectFoundSpots];
              incorrectSpots.push({
                point: {
                  x: `${
                    ((e.pageX - cords.left) / rootRef.current.clientWidth) * 100
                  }%`,
                  y: `${
                    ((e.pageY - cords.top) / rootRef.current.clientHeight) * 100
                  }%`,
                },
                state: 'enter',
              });
              setIncorrectFoundSpots(incorrectSpots);
            }
          }}
        ></Image>
        {spots.map((spot, index) => {
          const topLeftPoint = spot.points![0]!;
          const bottomRightPoint = spot.points![1]!;
          const isFound = !!state[spot.key]?.correct;
          const isInteractive = !isViewOnly && !showResults;

          return (
            <Box
              key={spot.key}
              backgroundColor={
                spot.showArea || debug || showCorrect
                  ? 'rgba(16,46,142,0.15)'
                  : undefined
              }
              aria-label={spot.answer ? `Click area: ${spot.answer}` : 'Click area'}
              aria-pressed={isFound}
              role="button"
              tabIndex={isInteractive ? 0 : -1}
              position="absolute"
              top={topLeftPoint.y}
              left={topLeftPoint.x}
              right={`${100 - parseFloat(String(bottomRightPoint.x))}%`}
              bottom={`${100 - parseFloat(String(bottomRightPoint.y))}%`}
              _focusVisible={{ outline: '3px solid var(--stemi-focus-color)', outlineOffset: '2px', borderRadius: '4px' }}
              onClick={(e) => {
                e.stopPropagation();
                if (!isInteractive) return;
                if (rootRef.current) {
                  const cords = getCoords(rootRef.current);
                  const point = {
                    x: `${((e.pageX - cords.left) / rootRef.current.clientWidth) * 100}%`,
                    y: `${((e.pageY - cords.top) / rootRef.current.clientHeight) * 100}%`,
                  };
                  handleSpotActivation(spot, point);
                }
              }}
              onKeyDown={(e: React.KeyboardEvent) => {
                if (!isInteractive) return;
                if (e.key === ' ' || e.key === 'Enter') {
                  e.preventDefault();
                  const centerX = (parseFloat(String(topLeftPoint.x)) + parseFloat(String(bottomRightPoint.x))) / 2;
                  const centerY = (parseFloat(String(topLeftPoint.y)) + parseFloat(String(bottomRightPoint.y))) / 2;
                  handleSpotActivation(spot, { x: `${centerX}%`, y: `${centerY}%` });
                }
              }}
            />
          );
        })}
        {foundSpots.map((point, index, arr) => {
          return (
            <CheckIconComponent
              key={index}
              point={point}
              animate={index === arr.length - 1}
            />
          );
        })}
        {incorrectFoundSpots.map((spot, index, arr) => {
          return (
            <XIconComponent
              key={index}
              point={spot.point}
              state={spot.state}
              updateSelf={(state) => {
                const newIncorrect = [...incorrectFoundSpots];
                if (newIncorrect[index]) {
                  newIncorrect[index]!.state = state;
                }
                setIncorrectFoundSpots(newIncorrect);
              }}
              removeSelf={() => {
                const newIncorrect = [...incorrectFoundSpots];
                newIncorrect.splice(index);
                setIncorrectFoundSpots(newIncorrect);
              }}
            />
          );
        })}
      </Box>
      <HStack
        minW={showResults ? '300px' : undefined}
        spacing={0}
        gap={2}
        justify="space-between"
      >
        {showResults && (
          <>
            {showResults && (
              <IconButton
                size="sm"
                colorScheme="stemiBlue"
                variant="ghost"
                aria-label="reset"
                icon={<RefreshIcon boxSize="24px" />}
                onClick={_reset}
              >
                {t('plugin.imageClickHotspots.reset')}
              </IconButton>
            )}
            <HStack>
              <span>
                {shownCorrect} / {spots.length}
              </span>
            </HStack>
          </>
        )}
        <HStack>
          {!isViewOnly && (
            <Button colorScheme="blue" onClick={_submit}>
              {t('plugin.imageClickHotspots.submit')}
            </Button>
          )}
        </HStack>
      </HStack>
    </VStack>
  );

  if (debug) {
    return (
      <VStack w="full">
        {ret}
        <Text>
          <pre>{JSON.stringify(state, null, 2)}</pre>
        </Text>
      </VStack>
    );
  }

  return <>{ret}</>;
};

export default ImageHotspots;
